// Private Yoga Warminster - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const PrivateYogaWarminster = () => {
  usePageMeta({
    title: "Private Yoga Warminster | 1-to-1 Yoga | Annelie Lily",
    description: "Private yoga sessions for Warminster residents. One-to-one yoga tailored to your body and goals, based in Frome, just 15 minutes from Warminster.",
    canonical: "https://annelielily.com/areas/yoga-warminster/private-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Private Yoga Sessions for Warminster Residents",
    description: "One-to-one private yoga sessions for Warminster residents at Annelie Lily Yoga in Frome. Personalised practice for your needs.",
    serviceType: "Private Yoga Sessions",
    url: "https://annelielily.com/areas/yoga-warminster/private-yoga"
  });

  const faqs = [
    { question: "What happens in a private yoga session?", answer: "We start with a conversation about your goals and any health considerations. Then I design a practice entirely around you — the poses, pace, and focus all adapt to what you need." },
    { question: "How far is Frome from Warminster?", answer: "Just 15 minutes via the A362. It's a straightforward drive, and many Warminster residents come weekly." },
    { question: "Is private yoga suitable for complete beginners?", answer: "Absolutely. Private sessions are ideal for beginners — learn the foundations properly with individual attention before joining group classes, or continue one-to-one." },
    { question: "Can I work on specific issues like flexibility?", answer: "Yes. Many students come with specific goals — flexibility, strength, stress management, or working with injuries. We tailor every session to what you need." },
    { question: "How long are sessions?", answer: "Typically 60 minutes, though 90-minute sessions are available. We work at your pace without rushing." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Private Yoga" currentSlug="areas/yoga-warminster/private-yoga" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Private Yoga for Warminster</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Private Yoga Sessions Warminster</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              One-to-one yoga for Warminster residents. A practice designed entirely around you — just 15 minutes from Warminster.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />15 min from Warminster</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Private Session <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is private yoga?</h2>
          <p className="text-neutral-700">
            Private yoga is one-to-one instruction where every aspect of the session is tailored to you. The poses, pace, and focus all adapt to your body and goals — ideal for beginners, those with injuries, or anyone wanting individual attention.
          </p>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who are private sessions for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Learn the foundations with individual attention and build confidence" },
              { title: "Working with injuries", desc: "Back pain, joint issues, recovery — we adapt practice to your body" },
              { title: "Specific goals", desc: "Flexibility, strength, stress management, or preparing for something" },
              { title: "Prefer one-to-one", desc: "Some people work better with individual attention, without others around" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a session</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Initial conversation</h3>
                <p className="text-sm">We discuss your goals, any injuries, and what you want from the session.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Tailored practice</h3>
                <p className="text-sm">Drawing on <Link to="/areas/yoga-warminster/hatha-yoga" className="text-gold hover:underline">Hatha</Link> or <Link to="/areas/yoga-warminster/yin-yoga" className="text-gold hover:underline">Yin</Link>, I guide you through a practice designed for your body.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Individual attention</h3>
                <p className="text-sm">One-to-one focus allows detailed alignment cues and adjustments not possible in groups.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Guided relaxation</h3>
                <p className="text-sm">Every session ends with proper relaxation — time for your body to integrate the practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Warminster residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              <Link to="/areas/yoga-warminster" className="text-gold hover:underline">Warminster</Link> is just 15 minutes from Frome via the A362 — close enough that private yoga sessions can become a regular part of your routine. Many Warminster residents make this short journey weekly.
            </p>
            <p>
              The studio offers a calm, private environment away from town — important for sessions where we need space to work without interruption.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of private yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, text: "Undivided attention" },
              { icon: Clock, text: "Flexible scheduling" },
              { icon: Heart, text: "Adapted to your body" },
              { icon: Check, text: "Faster progress" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions about private yoga in Warminster</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher with experience working one-to-one with students of all levels. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to book a private session?</h2>
          <p className="text-neutral-600 mb-6">Get in touch to discuss your goals and find a time that works.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Session <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Warminster residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-warminster/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga Warminster</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-warminster/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga Warminster</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-warminster" className="text-sm text-gold hover:underline">All Classes Warminster</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/private-yoga" className="text-sm text-gold hover:underline">Private Sessions (Main)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateYogaWarminster;
